/**
 * Turning a payoff into a drawing — ROADMAP 6.8.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ARITHMETIC IS IN `@apna/shared`; THIS IS ONLY THE VIEW
 *
 * `payoffCurve`, `payoffShape` and `breakevens` (6.7) are exact and have no
 * opinion about pixels. What belongs here is everything a CHART has to decide
 * and a valuation does not: how wide a window to show, how to scale an axis
 * when one tail runs off the top of it, and where the spot sits.
 *
 * AN UNBOUNDED TAIL CANNOT SET THE SCALE
 *
 * The obvious y-axis is max-profit to max-loss. It breaks on the first naked
 * short: `payoffShape` reports that tail as `UNBOUNDED`, correctly, and there
 * is no number to scale to. Scaling to the loss at some arbitrary far spot
 * instead squashes everything that matters — the credit, the breakevens, the
 * whole body of the position — into a few pixels around zero.
 *
 * So the axis is set by what is INSIDE the window, which is also all the
 * reader can see. A tail leaving the frame is drawn leaving the frame, and
 * the max-loss label says "unbounded" rather than inventing a figure for it.
 *
 * THE WINDOW COVERS THE STRIKES, NOT A FIXED PERCENTAGE
 *
 * A ±7% band around the spot is the usual choice and it hides the wings of
 * anything wider — an Iron Condor's long legs can sit outside it on a quiet
 * index, so the chart shows an unbounded-looking short strangle for a
 * position that is fully hedged. The window covers every strike plus a
 * margin, and widens to at least ±5% so a single at-the-money option still
 * has somewhere to slope.
 *
 * AND IT ALWAYS COVERS THE BREAKEVENS — ROADMAP 9.68
 *
 * The strike spread alone is not enough, and a short straddle is the proof:
 * it has ONE strike, so the spread is whatever gap happens to sit between
 * that strike and the spot — a few hundred points — and the window came out
 * at ±5% of spot while the position broke even 4,000 points away in each
 * direction. Every pixel drawn was profit. The chart showed a hill, and the
 * only thing a short straddle is actually about is where it stops being one.
 *
 * So the breakevens are part of what the window must contain, with room past
 * them for the losing slope to be visible rather than clipped at the frame.
 * They are the most important feature of a payoff: the whole chart exists to
 * answer "between which prices does this make money".
 *
 * WHAT THIS DELIBERATELY DOES NOT DRAW
 *
 * The T+0 curve — what the position is worth TODAY rather than at expiry.
 * It needs each leg repriced at its own implied volatility, which needs a
 * FORWARD for that expiry, which 6.6 gets from the chain's at-the-money
 * straddle. This screen does not load a chain. Substituting the spot would
 * reintroduce exactly the basis artefact `greeks.ts` exists to avoid, and
 * on a payoff chart it would show as a curve sitting visibly wrong against
 * a position the trader can check. The expiry curve is drawn instead,
 * because it is exact and needs no model at all.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { breakevens, kinksOf, payoffAt, payoffCurve, type PayoffLeg } from '@apna/shared'

export interface PayoffWindow {
  readonly from: number
  readonly to: number
}

/** How far past the outermost strike the window reaches, as a fraction. */
const STRIKE_MARGIN = 0.25
/** The narrowest window, as a fraction of spot either side. */
const MIN_HALF_SPAN = 0.05
/** How much losing slope to show past a breakeven, as a fraction of spot. */
const BREAKEVEN_MARGIN = 0.03
/**
 * The widest the window may get, as a fraction of spot either side.
 *
 * A breakeven can legitimately sit a very long way out — a deep debit spread
 * is the honest case — and a window stretched to reach it would squash the
 * body of every other position into a band a few pixels tall. Past this the
 * breakeven is off the chart and the axis labels say where the chart ends,
 * which is the lesser of the two lies.
 */
const MAX_HALF_SPAN = 0.6

export function payoffWindow(
  legs: readonly PayoffLeg[],
  spot: number | null,
  /**
   * Breakevens to keep in frame.
   *
   * Passed in rather than solved here because a basket spanning expiries has
   * no at-expiry breakeven at all — its curve is a valuation on a date, and
   * the caller is the only one holding it. Omitted, they are solved from the
   * legs, which is right for every single-expiry basket.
   */
  breakevensOverride?: readonly number[],
): PayoffWindow {
  const strikes = kinksOf(legs)
  const centre =
    spot !== null && spot > 0 ? spot : (strikes[Math.floor(strikes.length / 2)] ?? 0)
  if (centre <= 0) return { from: 0, to: 1 }

  const minHalf = centre * MIN_HALF_SPAN
  const even = breakevensOverride ?? (legs.length > 0 ? breakevens(legs) : [])

  let from: number
  let to: number
  if (strikes.length === 0) {
    from = centre - minHalf
    to = centre + minHalf
  } else {
    // The spot is included so the marker is never off the edge of its own chart.
    const lo = Math.min(strikes[0]!, centre)
    const hi = Math.max(strikes[strikes.length - 1]!, centre)
    // Proportional to the SPREAD of the structure, so a 200-wide condor and a
    // 2,000-wide one both get room to show their flat tails.
    const margin = Math.max((hi - lo) * STRIKE_MARGIN, minHalf)
    from = lo - margin
    to = hi + margin
  }

  /*
   * WIDEN FOR THE BREAKEVENS, never narrow. A condor's breakevens sit
   * between its strikes and change nothing here; a straddle's sit far
   * outside them and are the entire point of the drawing.
   *
   * The pad is what makes the loss VISIBLE rather than a corner touching the
   * frame: without it the curve meets the axis exactly at the edge and the
   * chart still looks like it is all profit.
   */
  const pad = Math.max(centre * BREAKEVEN_MARGIN, minHalf * 0.4)
  for (const b of even) {
    if (!Number.isFinite(b) || b < 0) continue
    from = Math.min(from, b - pad)
    to = Math.max(to, b + pad)
  }

  // Clamped, for the reason `MAX_HALF_SPAN` gives.
  return {
    from: Math.max(0, Math.max(from, centre * (1 - MAX_HALF_SPAN))),
    to: Math.min(to, centre * (1 + MAX_HALF_SPAN)),
  }
}

export interface PayoffPlot {
  readonly window: PayoffWindow
  readonly points: readonly { readonly spot: number; readonly value: number }[]
  /** The extremes INSIDE the window — what sets the axis. */
  readonly lo: number
  readonly hi: number
  /** Breakevens that fall inside the window, ascending. */
  readonly breakevens: readonly number[]
  /** The payoff at the live spot, or null when there is no spot. */
  readonly atSpot: number | null
}

/**
 * Everything the SVG needs, in one pass.
 *
 * `payoffCurve` already injects every kink and breakeven whether or not an
 * even sample lands on one, so the drawn corners are the real corners rather
 * than whatever the sampling happened to catch.
 */
/**
 * A curve the LEGS cannot produce on their own — ROADMAP 9.67.
 *
 * A basket whose legs do not expire together has no single at-expiry payoff:
 * on the day the near leg settles, the far leg is still an option. The
 * caller solves that with `calendarShape` and hands the valuation in here,
 * so there is one drawing routine rather than two charts to keep in step.
 */
export interface PayoffModel {
  /** What the basket is worth at this spot, on the date the caller chose. */
  readonly valueAt: (spot: number) => number
  /** Solved by the caller, for the same reason `breakevens` is not re-derived below. */
  readonly breakevens: readonly number[]
}

export function payoffPlot(
  legs: readonly PayoffLeg[],
  spot: number | null,
  samples = 160,
  /**
   * An explicit span, overriding the one derived from the strikes.
   *
   * The payoff dock lets the reader widen or narrow the view, and a chart
   * that re-derived its own window would ignore the slider. Everything
   * else — the sampling, the extremes, the breakevens in view — follows
   * from the window, so this is the only thing that has to be injectable.
   */
  override?: PayoffWindow,
  /** When present, the curve is this valuation rather than settlement. */
  model?: PayoffModel,
): PayoffPlot {
  // The MODEL's breakevens when there is one: a calendar has none at expiry,
  // and solving the legs for some would frame the chart on the wrong curve.
  const window = override ?? payoffWindow(legs, spot, model?.breakevens)
  /*
   * STRIKES AND BREAKEVENS ARE STILL SAMPLED EXPLICITLY, model or not. Evenly
   * spaced samples alone round the corners off a payoff, and on a calendar
   * the peak sits at a strike — which is the whole trade.
   */
  const points = model
    ? modelCurve(model, legs, { ...window, samples })
    : payoffCurve(legs, { ...window, samples })

  /*
   * Zero is always in range, seeded here rather than discovered. A position
   * profitable across the whole window still needs its zero line drawn, or
   * the reader has no baseline to judge the shape against.
   */
  let lo = 0
  let hi = 0
  for (const p of points) {
    if (p.value < lo) lo = p.value
    if (p.value > hi) hi = p.value
  }
  if (lo === hi) {
    lo -= 1
    hi += 1
  }

  return {
    window,
    points,
    lo,
    hi,
    // From the shared solver, filtered — never re-derived here. A second
    // breakeven calculation is a second chance to disagree with the curve
    // drawn beside it.
    breakevens: (model ? model.breakevens : breakevens(legs)).filter(
      (b) => b >= window.from && b <= window.to,
    ),
    atSpot:
      spot !== null && spot > 0
        ? model
          ? model.valueAt(spot)
          : payoffAt(legs, spot)
        : null,
  }
}

/** `payoffCurve`'s sampling, against a supplied valuation. */
function modelCurve(
  model: PayoffModel,
  legs: readonly PayoffLeg[],
  opts: { from: number; to: number; samples: number },
): { spot: number; value: number }[] {
  const from = Math.max(0, Math.min(opts.from, opts.to))
  const to = Math.max(from, opts.from, opts.to)
  const spots = new Set<number>()
  if (to === from) {
    spots.add(from)
  } else {
    for (let i = 0; i <= opts.samples; i += 1) spots.add(from + ((to - from) * i) / opts.samples)
    for (const leg of legs) {
      if (leg.strike !== null && leg.strike >= from && leg.strike <= to) spots.add(leg.strike)
    }
    for (const b of model.breakevens) {
      if (b >= from && b <= to) spots.add(b)
    }
  }
  return [...spots].sort((a, b) => a - b).map((spot) => ({ spot, value: model.valueAt(spot) }))
}

/** Map a value to a y coordinate in a `height`-tall box, profit upward. */
export function scaleY(value: number, lo: number, hi: number, height: number, pad: number): number {
  const span = hi - lo || 1
  const usable = height - pad * 2
  // SVG y grows downward, so profit is subtracted from the bottom.
  return height - pad - ((value - lo) / span) * usable
}

/** Map a spot to an x coordinate between `padLeft` and `width - padRight`. */
export function scaleX(
  spot: number,
  window: PayoffWindow,
  width: number,
  padLeft: number,
  padRight: number,
): number {
  const span = window.to - window.from || 1
  return padLeft + ((spot - window.from) / span) * (width - padLeft - padRight)
}

/**
 * Which spot a pixel column stands for — the inverse of `scaleX`, 9.47.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WRITTEN BESIDE ITS FORWARD, AND TESTED AS A ROUND TRIP
 *
 * A hover readout is the forward mapping run backwards, and the two drifting
 * apart is the failure that cannot be seen: the crosshair lands a few pixels
 * off the cursor and reads a P&L for a spot the reader did not point at. So
 * it lives in this file, next to `scaleX`, and the test asserts the round
 * trip rather than the formula.
 *
 * CLAMPED TO THE WINDOW. A pointer may be over the axis gutters — the y-axis
 * labels occupy 68px on the left — and extrapolating there would read a P&L
 * for a spot that is not on the chart at all.
 * ─────────────────────────────────────────────────────────────────────────────
 */
export function spotAtX(
  px: number,
  window: PayoffWindow,
  width: number,
  padLeft: number,
  padRight: number,
): number {
  const plotWidth = width - padLeft - padRight
  if (plotWidth <= 0) return window.from
  const t = Math.min(1, Math.max(0, (px - padLeft) / plotWidth))
  return window.from + t * (window.to - window.from)
}

/**
 * The sampled point nearest a spot, by index.
 *
 * The readout snaps to a SAMPLE rather than interpolating, so the dot it
 * draws sits exactly on the drawn curve. An interpolated value would be
 * correct and would still render a dot slightly off the line at a kink,
 * which reads as a drawing bug on the one feature whose job is precision.
 *
 * A linear scan: 160 points, once per pointer move, is nothing next to the
 * paint it triggers, and a binary search here would be a second thing that
 * can be wrong by one.
 */
export function nearestPointIndex(
  points: readonly { readonly spot: number }[],
  spot: number,
): number {
  let best = -1
  let bestGap = Infinity
  for (let i = 0; i < points.length; i += 1) {
    const gap = Math.abs(points[i]!.spot - spot)
    if (gap < bestGap) {
      bestGap = gap
      best = i
    }
  }
  return best
}

/** What the crosshair reads at one spot. */
export interface PayoffScrub {
  readonly spot: number
  /** Value at expiry, exact. */
  readonly expiry: number
  /** Value today, when a forward was supplied. Null otherwise. */
  readonly today: number | null
  /** Index into `plot.points`, so the caller draws on the curve it sampled. */
  readonly index: number
}

/**
 * Read the curves at a pixel column.
 *
 * Null when there is nothing under the pointer to read — an empty plot. The
 * caller then draws no crosshair rather than one at an invented spot.
 */
export function scrubAt(
  plot: PayoffPlot,
  px: number,
  width: number,
  padLeft: number,
  padRight: number,
  todayValues: readonly number[] | null,
): PayoffScrub | null {
  if (plot.points.length === 0) return null
  const spot = spotAtX(px, plot.window, width, padLeft, padRight)
  const index = nearestPointIndex(plot.points, spot)
  if (index < 0) return null
  const point = plot.points[index]!
  const today = todayValues?.[index]
  return {
    spot: point.spot,
    expiry: point.value,
    today: today !== undefined && Number.isFinite(today) ? today : null,
    index,
  }
}
